import './BetSelection.less';

import React, { FC, useContext } from 'react';
import { Map } from 'immutable';
import clsx from 'clsx';
import { localizeMap } from '../utils';
import { BetState, MarketLayoutCell, MarketLayoutCellType } from '../dto';
import { matchName$$ } from '../push/overview/subs';
import { useReframe } from 'nike-re-framejs';
import { isMobile } from 'react-device-detect';
import useMarketHover from '../hooks/useMarketHover';
import TurboLiveContext from '../context/TurboLiveContext';
import { useSportCode } from '../context/SportOverviewContext';
import { formatNumber } from '../../../common/util';
import { SvgIcon } from '../../../icons/SvgIcon';
import BetLocationContext, { betLocation } from '../../../common/BetLocationContext';
import { OddsChange, useOddsChange } from '../hooks/useOddsChange';

export const Cell: FC<{
    cell: MarketLayoutCell;
    bet: Map<string, any>;
    channelEnabled: boolean;
    hideBlocked?: boolean;
    isDetail?;
    narrow?: boolean;
    columnId?;
    index?: number;
    otherClass?: string;
}> = ({ cell, bet, channelEnabled, hideBlocked, isDetail, narrow, columnId, index, otherClass }) => {
    const labelSource = isDetail ? 'detail' : 'overview';
    const selections = bet.get('selections');
    const isBetStopped = bet.get('state') === BetState.Stopped;
    if (!cell) {
        return hideBlocked ? null : <EmptyCell />;
    }
    // workaround for invalid data (missing selection on bet)
    if (cell.type === MarketLayoutCellType.selection && !selections.has(cell.selectionIndex)) {
        return <EmptyCell />;
    }

    switch (cell.type) {
        case MarketLayoutCellType.selection:
            return (
                <BetSelection
                    selection={selections.get(cell.selectionIndex)}
                    betId={bet.get('id')}
                    channelEnabled={channelEnabled}
                    hideBlocked={hideBlocked}
                    stopped={isBetStopped}
                    otherClass={clsx(otherClass, { 'bg-white': isDetail })}
                    isDetail={isDetail}
                    columnId={columnId}
                    index={index}
                />
            );
        case MarketLayoutCellType.matchName:
            return <MatchNameCell matchId={bet.get('matchId')} />;
        case MarketLayoutCellType.betName:
            return <TextCell text={localizeMap(bet.get('betName'))} bold={cell.bold} hasBgr={isDetail} />;
        case MarketLayoutCellType.betParticipant:
            return (
                <TextCell
                    text={localizeMap(bet.getIn(['participants', cell.betParticipantIndex]))}
                    bold={cell.bold}
                    center={!isDetail}
                    hasBgr={isDetail}
                    grey
                />
            );
        case MarketLayoutCellType.label:
            return (
                <TextCell
                    text={localizeMap(bet.getIn([labelSource, 'label']))}
                    bold={cell.bold}
                    center={!isDetail}
                    orange={!isDetail}
                    hasBgr={isDetail}
                    narrow={isDetail && narrow}
                />
            );
        case MarketLayoutCellType.betParam:
            return <TextCell text={bet.get('betParam')} center={!isDetail} bold={cell.bold} orange hasBgr={isDetail} />;
        case MarketLayoutCellType.empty:
        default:
            return <EmptyCell />;
    }
};

export const BetSelectionLocked = () => <SvgIcon imgId="locked" sizeX="16" sizeY="16" className="bg-ico" />;

export const BetSelection = ({
    selection,
    betId,
    channelEnabled,
    hideBlocked,
    stopped,
    isDetail,
    localized,
    otherClass,
    columnId,
    index,
    actionAfterAddPick
}: {
    selection: Map<string, any>;
    betId: string;
    channelEnabled: boolean;
    hideBlocked?: boolean;
    stopped: boolean;
    isDetail?;
    localized?: boolean;
    otherClass?;
    columnId?;
    index?: number;
    sectionOrigin?: any;
    actionAfterAddPick?: any;
}) => {
    const selectionCode = selection.get('code');
    const odds = selection.get('odds');
    const enabled = !stopped && channelEnabled;
    const locked = !selection.get('enabled');
    const selectionName = selection.get('name');
    const oddsName = localized ? selectionName : localizeMap(selectionName);

    const { addPick, removePick, isPickSelectedSub$$ } = useContext(TurboLiveContext);
    const { betLocationSection } = useContext(BetLocationContext) || {};
    const derefSub = useReframe();
    const isPickSelected = derefSub(isPickSelectedSub$$(betId, selectionCode));
    const onClick = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!enabled || locked) {
            return;
        }
        if (isPickSelected) {
            removePick(betId);
        } else {
            addPick(
                betId,
                selectionCode,
                odds,
                betLocationSection || getSectionOrigin(isDetail, columnId),
                actionAfterAddPick ? actionAfterAddPick(e) : null
            );
        }
    };
    const [hoverRef] = useMarketHover(columnId, index);
    const oddsChange = useOddsChange(odds, betId);
    const formatOdds = locked ? <BetSelectionLocked /> : formatNumber(odds);
    const betExtraBold = selection.get('bold');
    const dataAtid = locked ? 'tl-bet-lock' : 'tl-bet-odd';
    const oddInc = oddsChange === OddsChange.Increased;
    const oddDec = oddsChange === OddsChange.Decreased;

    if (hideBlocked && locked) {
        return null;
    }

    return (
        <button
            ref={hoverRef}
            onClick={e => onClick(e)}
            data-atid={dataAtid}
            className={clsx('bet btn-no-pad', otherClass, {
                'text-extra-bold': betExtraBold,
                'text-bold': !betExtraBold && !isDetail,
                'c-black-30': !enabled,
                'c-black-100': enabled,
                selected: isPickSelected,
                'fs-13': !isDetail,
                'flex-col justify-evenly fs-14': isDetail && isMobile,
                'justify-between': isDetail && !isMobile,
                'flex items-center': isDetail,
                'flex-center': !isDetail && locked,
                'bet-wrap': hideBlocked,
                'flex-1': !hideBlocked,
                'bet-dec': !isDetail && oddDec,
                'bet-inc': !isDetail && oddInc,
                'bet-dec-detail': isDetail && oddDec,
                'bet-inc-detail': isDetail && oddInc
            })}
            disabled={!enabled || locked}
        >
            {isDetail ? (
                <>
                    <span
                        className={clsx('ellipsis bet-selection-name', {
                            'fs-12': isMobile,
                            'pl-10': !isMobile,
                            'c-white': isPickSelected,
                            'c-black-50': !isPickSelected && isMobile
                        })}
                        title={oddsName}
                    >
                        {oddsName}
                    </span>
                    <span className={clsx('text-extra-bold px-10', { 'fs-0': locked })}>{formatOdds}</span>
                </>
            ) : (
                formatOdds
            )}
        </button>
    );
};

const EmptyCell = () => <div className="flex-1 bet" />;

export const EmptyCellOverview = () => <div className="bet flex-1 flex-center c-black-100">-</div>;

const MatchNameCell: FC<{ matchId: string }> = ({ matchId }) => {
    const derefSub = useReframe();
    const sportCode = useSportCode();
    const matchName = derefSub(matchName$$(sportCode, matchId));
    return <TextCell text={matchName} />;
};

export const TextCell: FC<{
    text;
    bold?;
    orange?;
    grey?;
    narrow?;
    center?: boolean;
    hasBgr?: boolean;
}> = ({ text, bold, orange, grey, narrow, center, hasBgr }) => {
    return (
        <div className={clsx(`bet flex items-center ${narrow ? 'bet-narrow' : 'flex-1'}`, { 'bet-text-cell': hasBgr })}>
            <span
                title={text}
                className={clsx('fs-12 bet-text flex-1', {
                    'bet-orange': orange,
                    'c-black-50': grey,
                    'c-black-100': !orange && !grey,
                    'text-bold': bold,
                    'justify-center text-center': center,
                    'pl-10': !center
                })}
            >
                {text}
            </span>
        </div>
    );
};

const getSectionOrigin = (isDetail, colIndex) => {
    if (isDetail) {
        return betLocation.TURBOLIVE_DETAIL;
    }
    switch (colIndex) {
        case 1:
            return betLocation.TURBOLIVE_OVERVIEW_COL2;
        case 2:
            return betLocation.TURBOLIVE_OVERVIEW_COL3;
        case 3:
            return betLocation.TURBOLIVE_OVERVIEW_COL4;
        case 4:
            return betLocation.TURBOLIVE_OVERVIEW_COL5;
        case 5:
            return betLocation.TURBOLIVE_OVERVIEW_COL6;
        case 6:
            return betLocation.TURBOLIVE_OVERVIEW_COL7;
        case 7:
            return betLocation.TURBOLIVE_OVERVIEW_COL8;
        default:
            return betLocation.TURBOLIVE_OVERVIEW_COL1;
    }
};

export const BetAdditionalLabel = ({ label }) =>
    label ? <span className="bet-label fs-11 ellipsis p-abs bet-orange text-bold text-center">{label}</span> : null;
